import React, { useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import { addToast } from './Notifications';
import { useModalLock } from '../hooks/useModalLock';
import './Auth.css';

const API = 'https://piggybank-cors.andresnicolas.com/routes';

function Auth({ onClose, onLogin }) {
  useModalLock(true);

  const [mode, setMode] = useState('login');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [pinSent, setPinSent] = useState(false);
  const [pinVerified, setPinVerified] = useState(false);

  const reset = () => {
    setUsername(''); setEmail(''); setPassword('');
    setPin(''); setNewPassword(''); setConfirmPassword('');
    setError(''); setSuccess('');
    setPinSent(false); setPinVerified(false);
  };

  const switchMode = (m) => { reset(); setMode(m); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    setLoading(true);

    try {
      if (mode === 'login') {
        const res  = await fetch(`${API}/login.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ username, password }),
        });
        const data = await res.json();
        if (!data.success) {
          setError(data.message);
          addToast(data.message, 'error');
          return;
        }
        localStorage.setItem('username', data.username);
        onLogin(data.username);
        addToast(`Welcome back, ${data.username}!`, 'success');
        onClose();

      } else if (mode === 'register') {
        const res  = await fetch(`${API}/register.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ username, email, password }),
        });
        const data = await res.json();
        if (!data.success) {
          setError(data.message);
          addToast(data.message, 'error');
          return;
        }
        localStorage.setItem('username', data.username);
        onLogin(data.username);
        addToast(`Welcome, ${data.username}!`, 'success');
        onClose();

      } else if (mode === 'forgot') {
        if (!pinSent) {
          const res  = await fetch(`${API}/forgot_password.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
          });
          const data = await res.json();
          if (!data.success) {
            setError(data.message);
            addToast(data.message, 'error');
            return;
          }
          setPinSent(true);
          setSuccess('PIN sent. Check your inbox.');
          addToast('PIN sent. Check your inbox.', 'success');

        } else if (!pinVerified) {
          const res  = await fetch(`${API}/verify_pin.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, pin }),
          });
          const data = await res.json();
          if (!data.success) {
            setError(data.message);
            addToast(data.message, 'error');
            return;
          }
          setPinVerified(true);
          setSuccess('');
          addToast('PIN verified.', 'success');

        } else {
          if (newPassword !== confirmPassword) {
            setError('Passwords do not match.');
            addToast('Passwords do not match.', 'error');
            return;
          }
          if (newPassword.length < 6) {
            setError('Password must be at least 6 characters.');
            addToast('Password must be at least 6 characters.', 'error');
            return;
          }
          const res  = await fetch(`${API}/reset_password.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, pin, password: newPassword }),
          });
          const data = await res.json();
          if (!data.success) {
            setError(data.message);
            addToast(data.message, 'error');
            return;
          }
          addToast('Password updated. You can now sign in.', 'success');
          setTimeout(() => switchMode('login'), 2000);
        }
      }
    } catch {
      setError('Could not connect to server.');
      addToast('Could not connect to server.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    if (mode === 'forgot') {
      if (!pinSent) return 'Forgot Password';
      if (!pinVerified) return 'Enter PIN';
      return 'New Password';
    }
    return '';
  };

  const getBtnLabel = () => {
    if (loading) return 'Loading...';
    if (mode === 'login') return 'Sign In';
    if (mode === 'register') return 'Create Account';
    if (mode === 'forgot') {
      if (!pinSent) return 'Send PIN';
      if (!pinVerified) return 'Verify PIN';
      return 'Reset Password';
    }
  };

  return (
    <div className="auth-overlay" onClick={onClose}>
      <div className="auth-box" onClick={(e) => e.stopPropagation()}>
        <button className="auth-close" onClick={onClose}>
          <FaTimes size={13} />
        </button>

        {(mode === 'login' || mode === 'register') && (
          <div className="auth-tabs">
            <button className={`auth-tab ${mode === 'login' ? 'active' : ''}`} onClick={() => switchMode('login')}>
              Sign In
            </button>
            <button className={`auth-tab ${mode === 'register' ? 'active' : ''}`} onClick={() => switchMode('register')}>
              Register
            </button>
          </div>
        )}

        {mode === 'forgot' && (
          <div className="auth-back">
            <button className="auth-back-btn" onClick={() => switchMode('login')}>← Back to Sign In</button>
            <h4 className="auth-title">{getTitle()}</h4>
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          {mode === 'login' && (
            <>
              <div className="auth-field">
                <label className="auth-label">Username</label>
                <input className="auth-input" type="text" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="your username" autoFocus />
              </div>
              <div className="auth-field">
                <label className="auth-label">Password</label>
                <input className="auth-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••" />
              </div>
              <button type="button" className="auth-link" onClick={() => switchMode('forgot')}>
                Forgot password?
              </button>
            </>
          )}

          {mode === 'register' && (
            <>
              <div className="auth-field">
                <label className="auth-label">Username</label>
                <input className="auth-input" type="text" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="your username" autoFocus />
              </div>
              <div className="auth-field">
                <label className="auth-label">Email</label>
                <input className="auth-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
              </div>
              <div className="auth-field">
                <label className="auth-label">Password</label>
                <input className="auth-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••" />
              </div>
            </>
          )}

          {mode === 'forgot' && !pinSent && (
            <div className="auth-field">
              <label className="auth-label">Email</label>
              <input className="auth-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" autoFocus />
            </div>
          )}

          {mode === 'forgot' && pinSent && !pinVerified && (
            <div className="auth-field">
              <label className="auth-label">PIN</label>
              <input
                className="auth-input auth-pin"
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                autoFocus
              />
            </div>
          )}

          {mode === 'forgot' && pinVerified && (
            <>
              <div className="auth-field">
                <label className="auth-label">New Password</label>
                <input className="auth-input" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="••••••" autoFocus />
              </div>
              <div className="auth-field">
                <label className="auth-label">Confirm Password</label>
                <input className="auth-input" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="••••••" />
              </div>
            </>
          )}

          {error   && <p className="auth-error">{error}</p>}
          {success && <p className="auth-success">{success}</p>}

          <button className="auth-btn" type="submit" disabled={loading}>
            {getBtnLabel()}
          </button>
        </form>
      </div>
    </div>
  );
}

export default Auth;